import React, { useState, useEffect } from "react";
// 直接具体路径，不走 components barrel，否则会形成 components ↔ 本文件 的自环
import { Form, FormItem } from "../Base/Form";
import { Button } from "../Base/Button";
import { Dialog } from "../Base/Dialog";
import { Toast } from "../Base/Toast";
import { EnhancedInput } from "../Base/Input/EnhancedInput/EnhancedInput";
import {
  ResetPasswordByEmailDialogProps,
  ResetPasswordByEmailFormValues,
} from "./ResetPasswordByEmailDialog.types";
import { useT } from "../../i18n/hooks";
import { useCountdown } from "../../hooks/useUtils";
import { sendForgetEmail, resetPassword } from "../../api/auth";
import { getEmailPattern } from "../../utils/phoneValidation";
import utils from "../../utils";
import "./ResetPasswordByEmailDialog.scss";

const TOO_MANY_ATTEMPTS_MESSAGE = "Too many password attempts";

/** send_forget_email / reset_password 返回的 message 与 i18n key 的映射 */
const EMAIL_RESET_ERROR_KEYS: Record<string, string> = {
  "Email is not registered": "auth.errorEmailNotRegistered",
};

export const ResetPasswordByEmailDialog: React.FC<
  ResetPasswordByEmailDialogProps
> = ({ visible, onClose, onSuccess, onTooManyAttempts }) => {
  const t = useT();
  const [form] = Form.useForm<ResetPasswordByEmailFormValues>();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof ResetPasswordByEmailFormValues, string>>
  >({});

  const {
    timeLeft: countdown,
    isActive: isRunning,
    start,
    reset,
  } = useCountdown(60);

  const emailPattern = getEmailPattern();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setLoading(false);
      setShowPassword(false);
      setFormErrors({});
      reset();
    }
  }, [visible, form, reset]);

  const handleGetEmailCode = async () => {
    try {
      const email = form.getFieldValue("email");
      if (!email) {
        form.setFields([
          { name: "email", errors: [t("auth.pleaseEnterEmail")] },
        ]);
        return;
      }
      if (!emailPattern.test(email)) {
        form.setFields([
          { name: "email", errors: [t("auth.pleaseEnterValidEmail")] },
        ]);
        return;
      }
      await sendForgetEmail(email.trim());
      reset();
      start();
    } catch (error: any) {
      const rawMessage = (error?.message ?? "").trim();
      if (
        rawMessage === TOO_MANY_ATTEMPTS_MESSAGE ||
        rawMessage.includes(TOO_MANY_ATTEMPTS_MESSAGE)
      ) {
        onClose();
        onTooManyAttempts?.();
        return;
      }
      const title =
        rawMessage && EMAIL_RESET_ERROR_KEYS[rawMessage]
          ? t(EMAIL_RESET_ERROR_KEYS[rawMessage])
          : rawMessage || t("auth.sendCodeFailed");
      Toast.show({
        type: "failed",
        title,
      });
    }
  };

  const handleResetPassword = async (
    values: ResetPasswordByEmailFormValues
  ) => {
    try {
      setLoading(true);
      setFormErrors({});

      await resetPassword({
        email: values.email.trim(),
        code: values.code,
        password: values.password,
        countryId: utils.getObject("countryId") || "PH",
      });

      Toast.success(t("auth.resetPasswordSuccess"));
      form.resetFields();
      (onSuccess ?? onClose)();
    } catch (error: any) {
      const message = (error?.message ?? "").trim();
      if (
        message === TOO_MANY_ATTEMPTS_MESSAGE ||
        message.includes(TOO_MANY_ATTEMPTS_MESSAGE)
      ) {
        onClose();
        onTooManyAttempts?.();
        return;
      }
      const title =
        message && EMAIL_RESET_ERROR_KEYS[message]
          ? t(EMAIL_RESET_ERROR_KEYS[message])
          : message || t("auth.resetPasswordFailed");
      Toast.show({
        type: "failed",
        title,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldsChange = () => {
    const errors = form.getFieldsError();
    const newErrors: Partial<
      Record<keyof ResetPasswordByEmailFormValues, string>
    > = {};
    errors.forEach(field => {
      const fieldName = field.name[0] as keyof ResetPasswordByEmailFormValues;
      if (field.errors.length > 0) {
        newErrors[fieldName] = field.errors[0];
      }
    });
    setFormErrors(newErrors);
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      className="reset-password-by-email-dialog"
      showCloseButton={true}
      closeButtonPosition="bottom-center"
    >
      <div className="reset-password-by-email-dialog__title">
        {t("auth.resetPassword")}
      </div>
      <div
        className="reset-password-by-email-dialog__form-wrap"
        style={
          {
            "--adm-color-background": "transparent",
            background: "transparent",
            backgroundColor: "transparent",
          } as React.CSSProperties
        }
      >
        <Form
          form={form}
          onFinish={handleResetPassword}
          onFieldsChange={handleFieldsChange}
          className="reset-password-by-email-dialog__form"
        >
          <FormItem
            name="email"
            rules={[
              { required: true, message: t("auth.pleaseEnterEmail") },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!emailPattern.test(value)) {
                    return Promise.reject(
                      new Error(t("auth.pleaseEnterValidEmail"))
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <EnhancedInput
              className="reset-password-by-email-dialog__email-input"
              placeholder={t("auth.pleaseInputYourEmail")}
              prefixIcon={{
                icon: "mail",
              }}
              error={!!formErrors.email}
            />
          </FormItem>

          <FormItem
            name="code"
            rules={[
              { required: true, message: t("auth.pleaseEnterCode") },
              { len: 6, message: t("auth.smsCodeLength") },
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseInputYourCode")}
              maxLength={6}
              prefixIcon={{
                icon: "message",
                color: "var(--menu-item-icon-color)",
              }}
              suffixContent={{
                text: isRunning
                  ? t("common.resendIn", { time: countdown })
                  : t("auth.send"),
                onClick: handleGetEmailCode,
                loading: false,
                disabled: isRunning,
                variant: "tertiary",
                size: "sm",
              }}
              error={!!formErrors.code}
            />
          </FormItem>

          <div className="reset-password-by-email-dialog__label">
            {t("auth.newPassword")}
          </div>

          <FormItem
            name="password"
            rules={[
              { required: true, message: t("auth.pleaseEnterPassword") },
              { min: 8, message: t("auth.passwordTooShort") },
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseInputYourPassword")}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              prefixIcon={{
                icon: "password",
              }}
              suffixContent={{
                icon: showPassword ? "eye" : "eye-closed",
                onClick: () => setShowPassword(!showPassword),
              }}
              error={!!formErrors.password}
            />
          </FormItem>

          <Button
            type="submit"
            size="md"
            variant="primary"
            loading={loading}
            className="reset-password-by-email-dialog__submit"
            block
          >
            {t("auth.resetPassword")}
          </Button>
        </Form>
      </div>
    </Dialog>
  );
};
